import palette from "assets/theme/base/palette";
import { Button, Card, Grid, FormLabel, Chip, TextField, Divider } from "@mui/material";
import MDAvatar from "components/MDAvatar";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import React, { useRef, useState, useEffect, useMemo } from "react";
import EditIcon from "@mui/icons-material/Edit";
import ChildrenTable from "./ChildrenTable";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { call } from "services/axiosService/core";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import PersonIcon from "@mui/icons-material/Person";
import Avatar from "@mui/material/Avatar";
import Icon from "@mui/material/Icon";
import ChangePassword from "./ChangePassword";
import ConfirmDialog from "examples/Dialog/ConfirmDialog";
import ManageSubscription from "./ManageSubscription";
import ManagePaymentMethods from "./ManagePaymentMethods";
import SplitSharedAccount from "./SplitSharedAccount";
import { useNavigate, useLocation } from "react-router-dom";
import { useMaterialUIController, setProfile, setPersona } from "context";
import Snackbar from "@mui/material/Snackbar";
import * as Sentry from "@sentry/browser";
import MuiAlert from "@mui/material/Alert";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const RequiredLabel = ({ children }) => {
  const { t } = useTranslation();
  return (
    <FormLabel
      sx={{
        display: "flex",
        color: "black",
        alignItems: "center",
        margin: "10px auto 2px",
        fontSize: "1.05rem",
        fontWeight: 900,
      }}
    >
      <span style={{ color: "red", fontSize: "small", marginRight: "5px" }}>*</span>
      {children}
      <span style={{ color: "red", fontSize: "small", marginLeft: "10px" }}>
        {t("formLabels.required")}
      </span>
    </FormLabel>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return null;
  }
};

const StatItem = ({ icon, label, value }) => (
  <MDBox display="flex" alignItems="center" gap={1.5} flex={1}>
    <MDBox
      sx={{
        width: 40, height: 40, borderRadius: "50%",
        bgcolor: `${palette.brand[500]}15`,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}
    >
      <Icon sx={{ color: palette.brand[500], fontSize: "20px !important" }}>{icon}</Icon>
    </MDBox>
    <MDBox>
      <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
        {label}
      </MDTypography>
      <MDTypography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.4 }}>
        {value}
      </MDTypography>
    </MDBox>
  </MDBox>
);

const GuardianProfile = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const open_payment = location.state?.openPayment || false;
  const add_child = location.state?.openAddChild || false;
  const open_manage_payment_method = location.state?.openManagePaymentMethod || false;

  const [openAddChild, setOpenAddChild] = useState(add_child);
  const [openPayment, setOpenPayment] = useState(open_payment);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [children, setChildren] = useState([]);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openProfilePictureModal, setOpenProfilePictureModal] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const { profile, persona } = controller;
  const [loader, setLoader] = useState(true);
  const [openChangePasswordDialog, setOpenChangePasswordDialog] = useState(false);
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);
  const [openManagePaymentMethodModal, setOpenManagePaymentMethodModal] = useState(open_manage_payment_method);
  const [openConvertToLearnerModal, setOpenConvertToLearnerModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const [convertLoading, setConvertLoading] = useState(false);
  const [openSplitAccountModal, setOpenSplitAccountModal] = useState(false);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  // A shared account is one login carrying the child's profile nested inside
  // it, so it is the only kind that has anything to separate out.
  const isSharedAccount = profile.type && profile.type.includes("Shared");

  const handleAccountSplit = (result) => {
    // The split moves the Cognito identity onto the new parent record, so the
    // session and password keep working and there is no sign-out. What changes
    // is the record this session resolves to and the role it carries, so swap
    // both rather than leaving a stale learner profile in context.
    const parentDetails = result?.parent_details;
    if (parentDetails) {
      setProfile(dispatch, parentDetails);
      setName(parentDetails.details?.name ?? "");
      setEmail(parentDetails.email ?? "");
    }
    setPersona(dispatch, "guardian");
    getChildrens();
    setOpenSnackbar(true);
    setSeverity("success");
    setAlertMessage(
      t("splitAccount.snackbarSuccess") || "Account split successfully"
    );
  };

  const handleConvertToLearner = () => {
    setConvertLoading(true);
    call(`users/${profile.id}/convert-to-learner`, "PUT", {})
      .then((response) => {
        if (response.status === 200) {
          setProfile(dispatch, response.data.user_details);
          setPersona(dispatch, "adultAthlete");
          setChildren([response.data.user_details, ...children]);
          setOpenSnackbar(true);
          setSeverity("success");
          setAlertMessage("Successfully converted to learner");
          setOpenConvertToLearnerModal(false);
          setTimeout(() => setOpenPayment(true), 100);
        } else {
          setOpenSnackbar(true);
          setSeverity("error");
          setAlertMessage("Error while converting to learner");
        }
        setConvertLoading(false);
      })
      .catch((error) => {
        Sentry.captureException(error);
        setOpenSnackbar(true);
        setSeverity("error");
        setAlertMessage("Error while converting to learner");
        setConvertLoading(false);
      });
  };

  useEffect(() => {
    if (persona !== "admin" && persona !== "contentManager") {
      getChildrens();
    }
    setName(profile.details.name ?? "");
    setEmail(profile.email ?? "");
  }, []);

  const getChildrens = () => {
    setLoader(true);
    call(`users/${profile.id}`, "GET", { params: { type: "children" } })
      .then((response) => {
        if (response.status === 200) {
          setChildren(response.data)
          setLoader(false);
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        setLoader(false);
      });
  };

  const firstName = name.split(" ")[0] || "";
  const memberSince = formatDate(profile.signup_at || profile.created_at || profile.signup_date);

  const roleLabel = persona === "adultAthlete"
    ? t("userDynamicData.athlete")
    : persona === "admin"
      ? t("profileInfo.admin")
      : persona === "contentManager"
        ? t("profileInfo.contentManager")
        : t("profileInfo.guardian");

  const actionCards = [
    {
      icon: "lock",
      label: t("buttonTitles.changePassword"),
      description: t("profileInfo.changePasswordDesc"),
      color: palette.brand[500],
      bg: `${palette.brand[500]}18`,
      onClick: () => setOpenChangePasswordDialog(true),
      show: true,
    },
    {
      icon: "email",
      label: t("headings.manageSubscription"),
      description: t("profileInfo.manageSubscriptionDesc"),
      color: "#9c27b0",
      bg: "#9c27b018",
      onClick: () => setOpenSubscriptionDialog(true),
      show: true,
    },
    {
      icon: "credit_card",
      label: t("buttonTitles.managePaymentMethods"),
      description: t("profileInfo.managePaymentMethodsDesc"),
      color: "#4caf50",
      bg: "#4caf5018",
      onClick: () => setOpenManagePaymentMethodModal(true),
      show: persona !== "admin" && persona !== "contentManager",
    },
    {
      icon: "call_split",
      label: t("buttonTitles.splitAccount"),
      description: t("profileInfo.splitAccountDesc"),
      color: "#0288d1",
      bg: "#0288d118",
      onClick: () => setOpenSplitAccountModal(true),
      // Only a shared account has a child nested inside it to separate out.
      show: isSharedAccount,
    },
    {
      icon: "transfer_within_a_station",
      label: t("buttonTitles.convertToLearner"),
      description: t("profileInfo.convertToLearnerDesc"),
      color: "#ff9800",
      bg: "#ff980018",
      onClick: () => setOpenConvertToLearnerModal(true),
      show: persona !== "admin" && persona !== "contentManager" && persona !== "adultAthlete",
    },
  ].filter((c) => c.show);

  return (
    <DashboardLayout>
      <DashboardNavbar title={t("pageTitles.profile", { name: firstName })} />

      {/* Profile Card */}
      <Card sx={{ p: { xs: 2.5, sm: 4 }, mt: 3, mb: 2.5, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
        <MDBox display="flex" alignItems="flex-start" gap={{ xs: 2, sm: 4 }} flexWrap="wrap">

          {/* Avatar */}
          <MDBox sx={{ position: "relative", flexShrink: 0, alignSelf: "center" }}>
            {profile.profile_image_url ? (
              <MDAvatar
                src={profile.profile_image_url}
                alt={name}
                sx={{ width: 130, height: 130, borderRadius: "50%", border: `3px solid ${palette.brand[100]}` }}
              />
            ) : (
              <Avatar sx={{ width: 130, height: 130, borderRadius: "50%", bgcolor: `${palette.brand[500]}18`, border: `3px solid ${palette.brand[100]}` }}>
                <PersonIcon sx={{ width: 80, height: 80, color: palette.brand[400] }} />
              </Avatar>
            )}
            <MDBox
              onClick={() => setOpenProfilePictureModal(true)}
              sx={{
                position: "absolute", bottom: 4, right: 4,
                width: 34, height: 34, borderRadius: "50%",
                bgcolor: palette.brand[500], display: "flex",
                alignItems: "center", justifyContent: "center",
                cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                border: "2px solid #fff",
                "&:hover": { bgcolor: palette.brand[600] },
              }}
            >
              <Icon sx={{ color: "#fff", fontSize: "16px !important" }}>photo_camera</Icon>
            </MDBox>
          </MDBox>

          {/* Info section */}
          <MDBox flex={1} minWidth={220}>
            {/* Name + badge + edit button */}
            <MDBox display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
              <MDTypography variant="h4" fontWeight="bold">{name}</MDTypography>
              <Chip
                label={roleLabel}
                size="small"
                sx={{
                  bgcolor: "#e8f5e9", color: "#4caf50",
                  fontWeight: "bold", fontSize: "0.72rem", height: 24, borderRadius: "6px",
                }}
              />
              <MDBox flex={1} />
              <MDButton
                variant="outlined"
                onClick={() => setOpenEditModal(true)}
                startIcon={<EditIcon />}
                sx={{
                  borderColor: palette.brand[300],
                  color: palette.brand[500],
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: "bold",
                  px: 2,
                  py: 0.75,
                  "&:hover": { borderColor: palette.brand[500], bgcolor: `${palette.brand[500]}0a` },
                }}
              >
                {t("headings.editProfile")}
              </MDButton>
            </MDBox>

            {/* Email */}
            <MDBox display="flex" alignItems="center" gap={1} mb={1}>
              <Icon sx={{ fontSize: "18px !important", color: "text.secondary" }}>mail_outline</Icon>
              <MDTypography variant="body2" color="text.secondary">{email}</MDTypography>
            </MDBox>

            <Divider sx={{ my: 2 }} />

            {/* Stats row */}
            <MDBox display="flex" flexWrap="wrap" gap={2}>
              {memberSince && (
                <StatItem icon="calendar_today" label={t("profileInfo.memberSince")} value={memberSince} />
              )}
              {children.length > 0 && persona !== "admin" && persona !== "contentManager" && (
                <StatItem
                  icon="people"
                  label="Learners"
                  value={`${children.length}`}
                />
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      {/* Account Actions */}
      <MDTypography variant="h5" fontWeight="bold" mb={1.5}>
        {t("profileInfo.accountActions")}
      </MDTypography>

      <Grid container spacing={2} mb={2.5}>
        {actionCards.map((card) => (
          <Grid item xs={12} sm={6} key={card.label}>
            <Card
              onClick={card.onClick}
              sx={{
                p: 2.5, borderRadius: 3, cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                transition: "box-shadow 200ms, transform 200ms",
                "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.12)", transform: "translateY(-2px)" },
              }}
            >
              <MDBox display="flex" alignItems="center" gap={2}>
                <MDBox
                  sx={{
                    width: 52, height: 52, borderRadius: "14px",
                    bgcolor: card.bg, display: "flex",
                    alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}
                >
                  <Icon sx={{ color: card.color, fontSize: "26px !important" }}>{card.icon}</Icon>
                </MDBox>
                <MDBox flex={1}>
                  <MDTypography variant="body2" fontWeight="bold" mb={0.25}>
                    {card.label}
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {card.description}
                  </MDTypography>
                </MDBox>
                <Icon sx={{ color: "text.disabled", fontSize: "20px !important", flexShrink: 0 }}>arrow_forward</Icon>
              </MDBox>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Learners section */}
      {persona !== "admin" && persona !== "contentManager" && (
        <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", mb: 2.5 }}>
          <ChildrenTable
            key={openPayment ? "payment-mode" : "default-mode"}
            childrenDetails={children}
            getChildrens={getChildrens}
            loader={loader}
            openAddChild={openAddChild}
            openPayment={openPayment}
          />
        </Card>
      )}

      {/* Dialogs */}
      {openProfilePictureModal && (
        <ProfilePictureDialog
          openProfilePictureModal={openProfilePictureModal}
          closeProfilePictureModal={() => setOpenProfilePictureModal(false)}
          profile={profile}
          t={t}
        />
      )}

      {openEditModal && (
        <ProfileEditDialog
          openEditModal={openEditModal}
          closeEditModal={() => setOpenEditModal(false)}
          profile={profile}
          setName={setName}
        />
      )}

      {openChangePasswordDialog && (
        <ChangePassword
          openChangePasswordDialog={openChangePasswordDialog}
          setOpenChangePasswordDialog={setOpenChangePasswordDialog}
          setOpenSnackbar={setOpenSnackbar}
          setSeverity={setSeverity}
          setAlertMessage={setAlertMessage}
        />
      )}

      {openSubscriptionDialog && (
        <ManageSubscription
          openSubscriptionDialog={openSubscriptionDialog}
          setOpenSubscriptionDialog={setOpenSubscriptionDialog}
          setOpenSnackbar={setOpenSnackbar}
          setSeverity={setSeverity}
          setAlertMessage={setAlertMessage}
        />
      )}

      {openManagePaymentMethodModal && (
        <ManagePaymentMethods
          handleCloseManagePaymentMethodModal={() => setOpenManagePaymentMethodModal(false)}
          setOpenSnackbar={setOpenSnackbar}
          setSeverity={setSeverity}
          setAlertMessage={setAlertMessage}
          t={t}
        />
      )}

      {openSplitAccountModal && (
        <SplitSharedAccount
          open={openSplitAccountModal}
          onClose={() => setOpenSplitAccountModal(false)}
          profile={profile}
          onSplit={handleAccountSplit}
          t={t}
        />
      )}

      {openConvertToLearnerModal && (
        <ConfirmDialog
          open={openConvertToLearnerModal}
          onClose={() => setOpenConvertToLearnerModal(false)}
          onConfirm={handleConvertToLearner}
          title={t("buttonTitles.convertToLearner")}
          message={
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: t("instructionToConvertToLearner.info") }}
            />
          }
          confirmLabel={t("buttonTitles.yes") || "Yes"}
          cancelLabel={t("buttonTitles.no") || "No"}
          loading={convertLoading}
          icon="transfer_within_a_station"
          iconColor="#ff9800"
        />
      )}

      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={severity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

const ProfilePictureDialog = ({ openProfilePictureModal, closeProfilePictureModal, profile, t }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [controller, dispatch] = useMaterialUIController();

  const removePhotoHandler = () => {
    setIsLoading(true);
    profile.profile_image_url = null;
    call(`users/${profile.id}`, "PUT", { body: profile })
      .then((res) => {
        if (res.status === 200) {
          setProfile(dispatch, profile);
          setIsLoading(false);
          closeProfilePictureModal();
        } else {
          setIsLoading(false);
          closeProfilePictureModal();
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        setIsLoading(false);
        closeProfilePictureModal();
      });
  };

  const uploadPhotoHandler = (e) => {
    setIsUploading(true);
    const data = new FormData();
    data.append("file", e.target.files[0]);
    call(`users/${profile.id}`, "PUT", {
      headers: { accept: "application/json", "Content-Type": "multipart/form-data" },
      body: data,
      params: { upload: "image" },
    })
      .then((res) => {
        if (res.status === 200) {
          profile.profile_image_url = res.data.profile_image_url;
          setProfile(dispatch, profile);
          setIsUploading(false);
          closeProfilePictureModal();
        } else {
          setIsUploading(false);
          closeProfilePictureModal();
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        setIsUploading(false);
        closeProfilePictureModal();
      });
  };

  return (
    <Dialog open={openProfilePictureModal} onClose={closeProfilePictureModal} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: "24px", overflowX: "hidden" } }}>

      <MDBox onClick={closeProfilePictureModal} sx={{ position: "absolute", top: 12, right: 12, zIndex: 10, width: 36, height: 36, borderRadius: "50%", bgcolor: palette.neutral[100], border: `1px solid ${palette.neutral[200]}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { bgcolor: palette.neutral[200] } }}>
        <Icon sx={{ fontSize: "18px !important", color: palette.neutral[600] }}>close</Icon>
      </MDBox>

      <MDBox sx={{ px: 4, pt: 5, pb: 4, textAlign: "center" }}>
        <MDBox sx={{ position: "relative", display: "inline-flex", mb: 2.5 }}>
          {profile.profile_image_url ? (
            <MDAvatar src={profile.profile_image_url} alt={t("imageAlts.athleteImage")} sx={{ width: 110, height: 110, borderRadius: "50%", border: `3px solid ${palette.brand[100]}` }} />
          ) : (
            <Avatar sx={{ width: 110, height: 110, borderRadius: "50%", bgcolor: palette.brand[50], border: `3px solid ${palette.brand[100]}` }}>
              <PersonIcon sx={{ width: 70, height: 70, color: palette.brand[400] }} />
            </Avatar>
          )}
          <MDBox
            onClick={() => document.getElementById("getFile").click()}
            sx={{ position: "absolute", bottom: 2, right: 2, width: 32, height: 32, borderRadius: "50%", bgcolor: palette.brand[500], display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.2)", "&:hover": { bgcolor: palette.brand[600] } }}
          >
            <Icon sx={{ color: "#fff", fontSize: "16px !important" }}>photo_camera</Icon>
          </MDBox>
        </MDBox>

        <MDTypography variant="h5" fontWeight="bold" mb={0.5}>{t("headings.uploadProfilePicture")}</MDTypography>

        <MDBox display="flex" flexDirection="column" gap={1.5}>
          <MDButton variant="contained" fullWidth onClick={() => document.getElementById("getFile").click()}
            sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", bgcolor: palette.brand[500], color: "#fff", fontWeight: "bold", "&:hover": { bgcolor: palette.brand[600] } }}>
            <Icon sx={{ mr: 1, fontSize: "18px !important" }}>upload</Icon>
            <span translate="no" className="notranslate">{isUploading ? t("buttonTitles.uploading") : t("buttonTitles.uploadPhoto")}</span>
          </MDButton>
          {profile.profile_image_url && (
            <MDButton variant="outlined" fullWidth onClick={removePhotoHandler}
              sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", borderColor: "#f44336", color: "#f44336", fontWeight: "bold" }}>
              <span translate="no" className="notranslate">{isLoading ? t("buttonTitles.removing") : t("buttonTitles.removePhoto")}</span>
            </MDButton>
          )}
        </MDBox>
        <input type="file" id="getFile" accept=".png,.jpg,.jpeg,.svg,.ico,.gif" onChange={uploadPhotoHandler} style={{ display: "none" }} />
      </MDBox>
    </Dialog>
  );
};

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#fafafa" } };

const ProfileEditDialog = ({ openEditModal, closeEditModal, profile, setName }) => {
  const { t } = useTranslation();
  const [controller, dispatch] = useMaterialUIController();
  const [firstName, setFirstName] = useState((profile.details.name || "").trim().split(/\s+/)[0] ?? "");
  const [lastName, setLastName]   = useState((profile.details.name || "").trim().split(/\s+/)[1] ?? "");
  const [saving, setSaving]       = useState(false);

  const editSubmitHandler = (e) => {
    e.preventDefault();
    setSaving(true);
    const nameValue = [firstName, lastName].map((s) => (s || "").trim()).filter(Boolean).join(" ");
    const body = {
      pk: profile.pk ?? "user",
      sk: profile.sk ?? `user[${profile.id}]`,
      details: {
        address: {
          city: profile.details.address.city,
          country: profile.details.address.country,
          state: profile.details.address.state,
          street: profile.details.address.street,
          zip: profile.details.address.zip,
        },
        dob: profile.details.dob,
        persona: profile.details.persona ?? null,
        name: nameValue,
        gender: profile.details.gender ?? null,
      },
      email: profile.email, phone: profile.phone, id: profile.id,
      cognito_user_id: profile.cognito_user_id, identities: profile.identities,
      profile_image_url: profile.profile_image_url, relationships: profile.relationships,
      status: profile.status, payment_status: profile.payment_status, type: profile.type,
    };
    call(`users/${profile.id}`, "PUT", { body })
      .then((res) => {
        setSaving(false);
        if (res.status === 200) { closeEditModal(); setProfile(dispatch, body); setName(res.data.ModifiedAttributes.details.name); }
        else if (res.status === 440) closeEditModal();
      })
      .catch((err) => { Sentry.captureException(err); setSaving(false); });
  };

  return (
    <Dialog open={openEditModal} onClose={closeEditModal} fullWidth maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "24px", overflowX: "hidden" } }}>

      <MDBox onClick={closeEditModal} sx={{ position: "absolute", top: 12, right: 12, zIndex: 10, width: 36, height: 36, borderRadius: "50%", bgcolor: palette.neutral[100], border: `1px solid ${palette.neutral[200]}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { bgcolor: palette.neutral[200] } }}>
        <Icon sx={{ fontSize: "18px !important", color: palette.neutral[600] }}>close</Icon>
      </MDBox>

      <MDBox sx={{ px: 4, pt: 5, pb: 4 }}>
        <MDBox textAlign="center" mb={3}>
          <MDBox sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: palette.brand[50], display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
            <Icon sx={{ fontSize: "40px !important", color: palette.brand[500] }}>manage_accounts</Icon>
          </MDBox>
          <MDTypography variant="h5" fontWeight="bold" mb={0.5}>{t("headings.editProfile")}</MDTypography>
        </MDBox>

        <form onSubmit={editSubmitHandler}>
          <MDBox mb={2.5}>
            <MDTypography variant="body2" fontWeight="bold" mb={0.75}>
              {t("formLabels.firstName")} <span style={{ color: "#f44336" }}>*</span>
            </MDTypography>
            <TextField fullWidth size="small" value={firstName} placeholder={t("formPlaceHolders.firstName")} required sx={fieldSx}
              onChange={(e) => { if (e.nativeEvent.data !== " ") setFirstName(e.target.value); }} />
          </MDBox>

          <MDBox mb={3.5}>
            <MDTypography variant="body2" fontWeight="bold" mb={0.75}>
              {t("formLabels.lastName")} <span style={{ color: "#f44336" }}>*</span>
            </MDTypography>
            <TextField fullWidth size="small" value={lastName} placeholder={t("formPlaceHolders.lastName")} required sx={fieldSx}
              onChange={(e) => { if (e.nativeEvent.data !== " ") setLastName(e.target.value); }} />
          </MDBox>

          <MDBox display="flex" gap={2}>
            <MDButton variant="outlined" fullWidth onClick={closeEditModal}
              sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", borderColor: palette.neutral[300], color: palette.neutral[600], fontWeight: "bold" }}>
              {t("buttonTitles.cancel") || "Cancel"}
            </MDButton>
            <MDButton type="submit" variant="contained" fullWidth disabled={saving}
              sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", bgcolor: palette.brand[500], color: "#fff", fontWeight: "bold", "&:hover": { bgcolor: palette.brand[600] }, "&.Mui-disabled": { bgcolor: palette.neutral[300], color: "#fff" } }}>
              <span translate="no" className="notranslate">{saving ? <CircularProgress size={20} color="inherit" /> : t("buttonTitles.saveChanges")}</span>
            </MDButton>
          </MDBox>
        </form>
      </MDBox>
    </Dialog>
  );
};

export default GuardianProfile;
